import { CardData, Connection } from '../types';
import { generateId, generateNextSequentialNumber } from './calculations';

interface ExcelRow {
  id: string | number;
  label?: string;
  tp?: number;
  tra?: number;
  pessoasSimultaneas?: number;
  pessoasSetor?: number;
  disponibilidade?: number;
  proximoId?: string | number;
}

interface FactoryExcelRow {
  Tipo?: string;
  Nome?: string;
  Demanda_Dia?: number;
  Produto?: string;
  CT?: number;
  Unidade_CT?: string;
  Operadores?: number;
  Disponibilidade?: number;
  Estoque_Unidades?: number;
  Estoque_Dias?: number;
  Observacoes?: string;
}

const normalizeText = (value: unknown) =>
  String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

const normalizeAvailability = (value: unknown) => {
  let normalized = Number(value);
  if (!Number.isFinite(normalized) || Number.isNaN(normalized)) normalized = 100;
  if (normalized <= 1 && normalized > 0) normalized *= 100;
  return normalized;
};

const isFactoryTemplate = (rows: any[]) => {
  const first = rows[0] ?? {};
  return 'Tipo' in first && 'Nome' in first && ('CT' in first || 'Demanda_Dia' in first || 'Estoque_Unidades' in first);
};

const buildFactoryVsmCards = (rows: FactoryExcelRow[]): CardData[] => {
  const validRows = rows.filter(row => normalizeText(row.Tipo) && normalizeText(row.Nome));
  const processRows = validRows.filter(row => normalizeText(row.Tipo) === 'processo');
  const clientRow = validRows.find(row => ['cliente', 'customer'].includes(normalizeText(row.Tipo)));
  const supplierRow = validRows.find(row => ['fornecedor', 'supplier'].includes(normalizeText(row.Tipo)));
  const pcpmRow = validRows.find(row => ['pcpm', 'controle', 'planejamento'].includes(normalizeText(row.Tipo)));

  const cards: CardData[] = [];
  const topY = 96;
  const flowY = 360;
  const startX = 240;
  const processWidth = 121;
  const inventoryWidth = 104;
  const flowGap = 36;
  const dailyDemand = Number(clientRow?.Demanda_Dia) || 0;

  const supplierId = generateId();
  const pcpmId = generateId();
  const clientId = generateId();

  cards.push({
    id: supplierId,
    tipo: 'atividade',
    status: 'ativo',
    atividade: supplierRow?.Nome || 'Fornecedor',
    vsmIcon: 'fornecedor',
    extType: 'FORNECEDOR',
    posicao: { x: startX - 64, y: topY },
    conexoes_saida: [],
    p: 100,
    tp: 0,
    lt: 0,
    tt: 0,
    layoutType: 'classic',
    numeroSequencial: generateNextSequentialNumber(cards),
  } as CardData);

  cards.push({
    id: pcpmId,
    tipo: 'atividade',
    status: 'ativo',
    atividade: 'PCPM',
    vsmIcon: 'info_eletro',
    posicao: { x: startX + Math.max(1, processRows.length - 1) * (processWidth + inventoryWidth + flowGap * 2) / 2 - 64, y: topY },
    conexoes_saida: [],
    p: 100,
    tp: 0,
    lt: 0,
    tt: 0,
    layoutType: 'classic',
    numeroSequencial: generateNextSequentialNumber(cards),
  } as CardData);

  cards.push({
    id: clientId,
    tipo: 'card_final',
    status: 'ativo',
    atividade: clientRow?.Nome || 'Cliente',
    vsmIcon: 'cliente',
    extType: 'CLIENTE',
    entrada: dailyDemand,
    vsmDemanda: dailyDemand,
    vsmDemandaUnidade: '/dia',
    unidadeDemanda: '/dia',
    produto: clientRow?.Produto || '',
    posicao: { x: startX + Math.max(1, processRows.length) * (processWidth + inventoryWidth + flowGap * 2) + 20, y: topY },
    conexoes_saida: [],
    p: 100,
    tp: 0,
    lt: 0,
    tt: 0,
    layoutType: 'classic',
    numeroSequencial: generateNextSequentialNumber(cards),
  } as CardData);

  const processCards: CardData[] = [];
  const flowSequence: CardData[] = [];
  let cursorX = startX;
  processRows.forEach((row, index) => {
    const processId = generateId();
    const inventoryUnits = Number(row.Estoque_Unidades) || 0;
    const manualDays = row.Estoque_Dias !== undefined && row.Estoque_Dias !== null && String(row.Estoque_Dias) !== ''
      ? Number(row.Estoque_Dias)
      : undefined;

    if (inventoryUnits > 0 || (manualDays ?? 0) > 0) {
      const inventoryCard = {
        id: generateId(),
        tipo: 'inventory',
        status: 'ativo',
        atividade: `Estoque ${index + 1}`,
        inventory: inventoryUnits,
        vsmDias: manualDays,
        posicao: { x: cursorX, y: flowY + 5 },
        conexoes_saida: [],
        p: 100,
        tp: 0,
        lt: 0,
        tt: 0,
        layoutType: 'classic',
        numeroSequencial: generateNextSequentialNumber(cards),
      } as CardData;
      cards.push(inventoryCard);
      flowSequence.push(inventoryCard);
      cursorX += inventoryWidth + flowGap;
    }

    const processCard = {
      id: processId,
      tipo: 'atividade',
      status: 'ativo',
      atividade: row.Nome || `Processo ${index + 1}`,
      vsmIcon: 'processo',
      vsmCT: Number(row.CT) || 0,
      vsmCTUnit: row.Unidade_CT || 'seg',
      vsmOp: Number(row.Operadores) || 1,
      vsmDisp: normalizeAvailability(row.Disponibilidade),
      pessoasSimultaneas: Number(row.Operadores) || 1,
      posicao: { x: cursorX, y: flowY },
      conexoes_saida: [],
      p: normalizeAvailability(row.Disponibilidade),
      tp: 0,
      lt: 0,
      tt: 0,
      layoutType: 'classic',
      numeroSequencial: generateNextSequentialNumber(cards),
    } as CardData;
    processCards.push(processCard);
    cards.push(processCard);
    flowSequence.push(processCard);
    cursorX += processWidth + flowGap;
  });

  const connect = (source: CardData | undefined, target: CardData | undefined, connectionType?: Connection['connectionType']) => {
    if (!source || !target) return;
    source.conexoes_saida = [...(source.conexoes_saida || []), {
      id: generateId(),
      targetId: target.id,
      sourceHandle: 'right',
      targetHandle: 'left',
      connectionType,
    } as Connection];
  };

  flowSequence.forEach((card, index) => connect(card, flowSequence[index + 1] ?? cards.find(c => c.id === clientId), 'push'));
  const pcpmCard = cards.find(c => c.id === pcpmId);
  const supplierCard = cards.find(c => c.id === supplierId);
  const clientCard = cards.find(c => c.id === clientId);
  connect(pcpmCard, supplierCard, 'vsm_info');
  connect(clientCard, pcpmCard, 'vsm_info');

  return cards;
};

export const importFromExcel = async (file: File): Promise<CardData[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const { read, utils } = await import('xlsx');
        const data = e.target?.result;
        if (!data) {
            reject("No data read");
            return;
        }

        const workbook = read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames.includes('Dados') ? 'Dados' : workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Read raw data
        const rows: any[] = utils.sheet_to_json(worksheet);

        if (isFactoryTemplate(rows)) {
          resolve(buildFactoryVsmCards(rows as FactoryExcelRow[]));
          return;
        }

        const legacyRows = rows as ExcelRow[];

        const cards: CardData[] = [];
        
        // Initial positioning for "Staircase" layout
        let currentX = 200;
        let currentY = 150;
        const STEP_X = 300; // Largura do card atividade (240px) + gap padrão (60px)
        const STEP_Y = 60; // Espaçamento vertical reduzido

        legacyRows.forEach((row) => {
          const id = row.id ? String(row.id) : generateId();
          
          // Normalize Availability to 0-100 scale
          // If value is <= 1 (e.g. 0.85), assume decimal and convert to 85.
          // If value is > 1 (e.g. 85), assume percentage and keep as 85.
          let normDisp = Number(row.disponibilidade);
          if (isNaN(normDisp)) normDisp = 100;
          
          if (normDisp <= 1 && normDisp > 0) {
              normDisp = normDisp * 100;
          }

          const connections: Connection[] = [];
          if (row.proximoId) {
              connections.push({
                  id: generateId(),
                  targetId: String(row.proximoId),
                  sourceHandle: 'right' // Default to simple flow
              });
          }

          // Gerar próximo número sequencial para cada card importado
          const nextSequentialNumber = generateNextSequentialNumber(cards);

          const card: CardData = {
            id: id,
            tipo: 'atividade', // Defaulting to activity
            status: 'ativo',
            atividade: row.label || 'Atividade Importada',
            
            // Map TP/TRA
            lt: Number(row.tp) || 0,
            tt: Number(row.tra) || 0,
            
            // Lean Metrics
            pessoasSimultaneas: Number(row.pessoasSimultaneas) || 1,
            pessoasSetor: Number(row.pessoasSetor) || 1,
            disponibilidade: normDisp,
            
            // Automatic Layout (Staircase)
            posicao: {
                x: currentX,
                y: currentY
            },
            
            conexoes_saida: connections,
            numeroSequencial: nextSequentialNumber
          };

          cards.push(card);

          // Increment Position
          currentX += STEP_X;
          currentY += STEP_Y;
        });

        resolve(cards);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};
